/**
 * @file components/ActionRunsPanel.jsx
 * @description Shows the post-response actions triggered by a single request.
 *
 * Fetched lazily — only when a log row is expanded — since most rows have no
 * actions and the log list would otherwise pay for a join it rarely needs.
 */

import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { listActionRuns } from '../api/actionRuns';
import JsonViewer from './JsonViewer';

const STATUS_STYLES = {
  success: 'bg-success',
  failed:  'bg-danger',
  running: 'bg-secondary',
};

const LEVEL_STYLES = {
  debug: 'text-muted',
  info:  'text-body',
  warn:  'text-warning',
  error: 'text-danger',
};

/**
 * @param {{ correlationId: string|null }} props
 */
export default function ActionRunsPanel({ correlationId }) {
  const { data: runs = [], isLoading, error } = useQuery({
    queryKey: ['actionRuns', correlationId],
    queryFn:  () => listActionRuns(correlationId),
    enabled:  Boolean(correlationId),
  });

  if (!correlationId) return null;
  if (isLoading) return <p className="small text-muted mb-0">Loading actions…</p>;
  if (error) return <p className="small text-danger mb-0">Could not load actions: {error.message}</p>;
  if (runs.length === 0) return <p className="small text-muted mb-0">No actions were triggered by this request.</p>;

  return (
    <div className="d-flex flex-column gap-3">
      {runs.map((run) => (
        <div key={run.id} className="border rounded p-2">
          <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
            <code className="fw-semibold">{run.action_name}</code>
            <span className={`badge ${STATUS_STYLES[run.status] || 'bg-secondary'}`}>{run.status}</span>
            {run.duration_ms != null && <span className="small text-muted">{run.duration_ms} ms</span>}
            <span className="small text-muted">{new Date(run.started_at).toLocaleString()}</span>
          </div>

          {run.error_message && (
            <p className="small text-danger mb-2"><i className="bi bi-exclamation-triangle me-1" />{run.error_message}</p>
          )}

          {run.logs?.length > 0 && (
            <ul className="list-unstyled mb-2 small font-monospace">
              {run.logs.map((line) => (
                <li key={line.id} className={LEVEL_STYLES[line.level] || 'text-body'}>
                  <span className="text-muted me-2">
                    {new Date(line.timestamp).toLocaleTimeString()}
                  </span>
                  <span className="text-uppercase me-2">{line.level}</span>
                  {line.message}
                  {line.data && <JsonViewer value={line.data} />}
                </li>
              ))}
            </ul>
          )}

          {run.result != null && (
            <>
              <p className="fw-semibold mb-1 small text-uppercase text-muted">Result</p>
              <JsonViewer value={run.result} />
            </>
          )}
        </div>
      ))}
    </div>
  );
}
